import { Marker } from 'maplibre-gl';
import {
  MODES, VELOTYPEN, routeVehicle, routeTransit, routeFootpath, connectionGeometry,
  fetchHoehenprofil, fetchVeloAbschnitte
} from '../data/routing.js';
import { fahrtVerlauf } from '../data/oev.js';
import { linienZeichen } from './linienzeichen.js';
import { geocode, rueckwaerts } from '../data/suche.js';
import { einpassen as passeEin } from '../map/grenzen.js';
import { ortsfeld } from './ortsfeld.js';
import { toast } from './toast.js';

const EMPTY = { type: 'FeatureCollection', features: [] };

/** Was man beim nächsten Mal nicht neu einstellen will. */
const SPEICHER = 'kgva.route';

function gemerktLesen() {
  try { return JSON.parse(localStorage.getItem(SPEICHER)) || {}; } catch { return {}; }
}

/**
 * Farben der Velo-Abschnitte. Die Route ist sonst schwarz; eingefärbt wird
 * nur, was man vorher wissen will. Die Reihenfolge ist die der Ruhe: Türkis
 * wie die Velowege in der Legende, Grün für eigenen Streifen auf der Strasse,
 * Schwarz für die gewöhnliche Nebenstrasse im Mischverkehr - und Karmin dort,
 * wo man ohne Streifen auf einer Durchgangsstrasse fährt.
 */
const ABSCHNITT_FARBEN = {
  getrennt: '#0A7E8C',
  streifen: '#2F7A4F',
  uebrig: '#141414',
  hauptOhne: '#B3123C'
};

const ABSCHNITT_TEXTE = {
  getrennt: 'abseits vom Verkehr',
  streifen: 'Radstreifen',
  hauptOhne: 'Hauptstrasse ohne Streifen',
  uebrig: 'Nebenstrasse'
};

function endpointMarker(letter) {
  const el = document.createElement('div');
  el.textContent = letter;
  el.className = 'route-marke';
  return el;
}

const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) : '');

/**
 * Der Wochentag, wenn die Verbindung nicht heute faehrt.
 *
 * Sonst stehen in der Liste nur Uhrzeiten - und wer am ersten Morgenzug auf
 * „frueher“ drueckt, landet beim letzten Zug des Vorabends, ohne es zu sehen.
 */
function tagWennAnders(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d.toDateString() === new Date().toDateString()) return null;
  return d.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

/* Die Formate der Felder `date` und `time` - beide in Ortszeit, nicht UTC. */
const alsFeldDatum = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const alsFeldZeit = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export function createRoutePanel({ map, onActivate, onStartNavigation, onSaveTrip }) {
  const points = { from: null, to: null };
  const markers = { from: null, to: null };
  const gemerkt = gemerktLesen();
  let mode = MODES.some((m) => m.id === gemerkt.mode) ? gemerkt.mode : 'auto';
  // Veloeinstellungen. Sie gelten über die Sitzung hinaus, nicht je Route -
  // wer ein Rennvelo hat, hat es auch morgen noch.
  let velo = { typ: 'Hybrid', huegel: false, verkehr: false, belag: false, ...(gemerkt.velo || {}) };
  // Hauptroute und Varianten. Leer, solange es nur einen Weg gibt.
  let fahrten = [];
  let wahl = 0;
  let controller;
  let nodes = null;
  let connections = [];
  let drawn = null;   // zuletzt gezeichnete Route, für den Style-Wechsel
  /**
   * Gesuchter Zeitpunkt. `eingabe` ist, was in den Feldern steht; `suche` ist,
   * wonach gerade gefragt wird. Beides getrennt, weil „früher“ und „später“
   * nur die Abfrage verschieben - die Felder sollen stehen bleiben, wie sie
   * eingestellt wurden.
   */
  let eingabe = { zeit: null, ankunft: false };
  let suche = { zeit: null, ankunft: false };
  /**
   * Hat jemand an Datum oder Uhrzeit gedreht?
   *
   * Die Felder stehen auf der aktuellen Zeit, damit man sieht, wonach gesucht
   * wird - leer sah aus, als waere nichts eingestellt. Solange sie unberuehrt
   * sind, bleibt die Suche aber wirklich bei "jetzt": sonst sucht das Panel
   * nach einer halben Stunde noch immer nach der Zeit, zu der es geoeffnet
   * wurde.
   */
  let zeitBeruehrt = false;
  let aktuelleZeitAnzeigen = null;
  let feldSichtbarkeit = null;
  let stationen = null;   // Haltestellen, die der Fahrplan genommen hat
  /**
   * Zählt jedes Zeichnen einer Verbindung. Die Strecken kommen aus mehreren
   * langsamen Abfragen; wer inzwischen eine andere Verbindung, ein anderes
   * Verkehrsmittel oder gar „Löschen“ gewählt hat, soll nicht von einer alten
   * Antwort überzeichnet werden.
   */
  let zeichenLauf = 0;

  function merken() {
    try { localStorage.setItem(SPEICHER, JSON.stringify({ mode, velo })); } catch { /* nicht kritisch */ }
  }

  /* --------------------------------------------------------- Kartenebenen */

  function ensureLayers() {
    if (map.getSource('route')) return;
    map.addSource('route', { type: 'geojson', data: EMPTY });
    map.addSource('route-stops', { type: 'geojson', data: EMPTY });
    map.addLayer({
      id: 'route-casing',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#FCFCFC', 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 9, 18, 14] }
    });
    map.addLayer({
      id: 'route-line',
      type: 'line',
      source: 'route',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        // Ohne `art` bleibt die Linie schwarz - nur die Veloauswertung setzt
        // die Eigenschaft, alle anderen Routen sehen aus wie bisher.
        'line-color': ['match', ['get', 'art'],
          'getrennt', ABSCHNITT_FARBEN.getrennt,
          'streifen', ABSCHNITT_FARBEN.streifen,
          'hauptOhne', ABSCHNITT_FARBEN.hauptOhne,
          '#141414'],
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 2, 14, 4.5, 18, 7],
        'line-dasharray': ['case', ['==', ['get', 'walk'], true], ['literal', [2, 1.5]], ['literal', [1, 0]]]
      }
    });
    map.addLayer({
      id: 'route-stops-layer',
      type: 'circle',
      source: 'route-stops',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 2.5, 14, 4],
        'circle-color': '#FCFCFC',
        'circle-stroke-color': '#141414',
        'circle-stroke-width': 1.5
      }
    });
  }

  function drawRoute(features, stops = [], { einpassen = true } = {}) {
    drawn = { features, stops };
    ensureLayers();
    map.getSource('route').setData({ type: 'FeatureCollection', features });
    map.getSource('route-stops').setData({
      type: 'FeatureCollection',
      features: stops.map((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} }))
    });
    // Solange der Verlauf noch geladen wird, gibt es nur Halte - darauf zoomen,
    // sonst steht die Karte woanders als die Verbindung. Wird dieselbe Route
    // bloss neu eingefärbt, bleibt der Ausschnitt, wie er ist.
    if (!einpassen) return;
    const all = features.length ? features.flatMap((f) => f.geometry.coordinates) : stops;
    if (all.length > 1) passeEin(map, all);
  }

  /** Eine Fahrt als einfache schwarze Linie zeichnen. */
  function zeichneFahrt(res, { einpassen = true } = {}) {
    drawRoute(
      [{ type: 'Feature', geometry: { type: 'LineString', coordinates: res.coordinates }, properties: {} }],
      [],
      { einpassen }
    );
  }

  /**
   * Variantenwahl. Die Liste steht über der Zusammenfassung, weil sie bestimmt,
   * worauf sich alles darunter bezieht. Der Ausschnitt bleibt beim Wechsel
   * stehen - die Varianten liegen ohnehin nebeneinander, und ein Sprung der
   * Karte bei jedem Antippen macht den Vergleich schwer.
   */
  function variantenWahl(signal) {
    const box = document.createElement('div');
    box.className = 'route__varianten';
    fahrten.forEach((f, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'route__variante';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(i === wahl));
      const k = document.createElement('strong');
      k.textContent = `${km(f.distanceKm)} km`;
      const t = document.createElement('small');
      t.textContent = `${f.durationMin} Min`;
      b.append(k, t);
      b.addEventListener('click', () => {
        if (i === wahl) return;
        wahl = i;
        zeichneFahrt(fahrten[i], { einpassen: false });
        renderVehicle(fahrten[i], signal);
      });
      box.append(b);
    });
    return box;
  }

  function clearRoute() {
    zeichenLauf += 1;
    drawn = null;
    if (!map.getSource('route')) return;
    map.getSource('route').setData(EMPTY);
    map.getSource('route-stops').setData(EMPTY);
  }

  /* -------------------------------------------------------------- Punkte */

  function setPoint(which, point, { rechnen = true } = {}) {
    points[which] = point;
    markers[which]?.remove();
    markers[which] = null;
    if (point) {
      markers[which] = new Marker({ element: endpointMarker(which === 'from' ? 'A' : 'B'), draggable: true })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
      markers[which].on('dragend', () => {
        const ll = markers[which].getLngLat();
        const neu = { lat: ll.lat, lng: ll.lng, name: `${ll.lat.toFixed(4)}, ${ll.lng.toFixed(4)}` };
        points[which] = neu;
        paintFields();
        run();
        // Im Feld steht lieber eine Adresse als zwei Zahlen. Kommt sie, ist die
        // Route längst unterwegs - sie ändert sich dadurch nicht.
        rueckwaerts(ll).then((a) => {
          if (a?.kurz && points[which] === neu) {
            neu.name = a.kurz;
            paintFields();
          }
        }).catch(() => {});
      });
    }
    paintFields();
    onActivate?.();
    if (rechnen) run();
  }

  function paintFields() {
    if (!nodes) return;
    nodes.fromInput.value = points.from?.name || '';
    nodes.toInput.value = points.to?.name || '';
  }

  /* ------------------------------------------------------------- Rechnen */

  /**
   * Rechnen und anzeigen. `frueherAls` ist fuer den Knopf „frueher“: eine
   * Antwort ohne frühere Abfahrt wird verworfen (Rueckgabe `false`), damit der
   * Aufrufer weiter zurueck fragen kann.
   */
  async function run({ frueherAls = null } = {}) {
    if (!nodes) return false;
    // Auch ohne neue Abfrage: was noch unterwegs ist, gilt nicht mehr. Sonst
    // zeichnet „Löschen“ die Linie weg, und die Antwort malt sie wieder hin.
    controller?.abort();
    zeichenLauf += 1;
    if (nodes.hint) nodes.hint.hidden = Boolean(points.from && points.to);
    if (!points.from || !points.to) {
      nodes.result.textContent = '';
      clearRoute();
      return false;
    }
    controller = new AbortController();
    const signal = controller.signal;

    nodes.result.textContent = '';
    const busy = document.createElement('p');
    busy.className = 'hint hint--block';
    busy.textContent = 'Berechne …';
    nodes.result.append(busy);

    try {
      if (mode === 'transit') {
        // Unberuehrte Felder wandern mit der Uhr mit - und gesucht wird dann
        // nach "jetzt", nicht nach dem Zeitpunkt des Oeffnens.
        if (!zeitBeruehrt && !frueherAls) {
          aktuelleZeitAnzeigen?.();
          suche = { zeit: null, ankunft: eingabe.ankunft };
        }
        const res = await routeTransit({
          from: points.from, to: points.to, zeit: suche.zeit, ankunft: suche.ankunft, signal
        });
        if (frueherAls && !res.connections.some((c) => new Date(c.departure).getTime() < frueherAls)) {
          return false;
        }
        connections = res.connections;
        stationen = { von: res.von, nach: res.nach };
        renderTransit();
        selectConnection(0);
      } else {
        const costing = MODES.find((m) => m.id === mode).costing;
        const res = await routeVehicle({
          from: points.from, to: points.to, costing, signal,
          velo: mode === 'bicycle' ? velo : undefined,
          // Nur beim Velo: hier gibt es oft zwei vernünftige Wege - kurz an der
          // Hauptstrasse oder etwas länger dem Fluss entlang. Beim Auto wäre
          // die Wahl meist dieselbe Strasse mit anderer Ausfahrt.
          varianten: mode === 'bicycle' ? 2 : 0
        });
        fahrten = res.varianten ? [res, ...res.varianten] : [];
        wahl = 0;
        zeichneFahrt(res);
        renderVehicle(res, signal);
      }
    } catch (err) {
      if (err.name === 'AbortError') return false;
      nodes.result.textContent = '';
      const p = document.createElement('p');
      p.className = 'error';
      // Ein nackter Netzfehler heisst im Browser "Failed to fetch".
      p.textContent = err instanceof TypeError ? 'Dienst nicht erreichbar. Besteht eine Verbindung?' : err.message;
      nodes.result.append(p);
      clearRoute();
      return false;
    }
    ergebnisZeigen();
    return true;
  }

  /* ----------------------------------------------------------- Ausgaben */

  /**
   * Auf dem Telefon füllen die Felder die Leiste - das Ergebnis läge darunter,
   * und man sähe nach dem Rechnen nichts Neues. Also dorthin rollen.
   */
  function ergebnisZeigen() {
    const tafel = nodes.result.closest('.view--info');
    // Neben der Karte ist die Spalte hoch genug - dort stört der Sprung nur.
    if (!tafel || tafel.clientHeight > 520 || tafel.scrollHeight <= tafel.clientHeight) return;
    nodes.result.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  function summaryLine(main, sub) {
    const box = document.createElement('div');
    box.className = 'route__summary';
    const a = document.createElement('strong');
    a.textContent = main;
    const b = document.createElement('span');
    b.textContent = sub;
    box.append(a, b);
    return box;
  }

  function renderVehicle(res, signal) {
    nodes.result.textContent = '';
    if (fahrten.length > 1) nodes.result.append(variantenWahl(signal));

    // Zielfuehrung nur, wo sie Sinn ergibt: mit Manoevern und mit Ortung.
    if (onStartNavigation && res.maneuvers?.length && navigator.geolocation) {
      const los = document.createElement('button');
      los.type = 'button';
      los.className = 'route__go';
      los.textContent = 'Navigation starten';
      los.addEventListener('click', () => {
        onStartNavigation({
          route: res,
          to: points.to,
          costing: MODES.find((m) => m.id === mode).costing,
          // Die Einstellungen müssen mit: nach einem Verfahrer rechnet die
          // Führung neu, und das soll mit demselben Velo geschehen.
          velo: mode === 'bicycle' ? velo : undefined
        });
      });
      nodes.result.append(los);
    }

    nodes.result.append(
      summaryLine(
        `${res.durationMin} Min`,
        `${res.distanceKm.toFixed(res.distanceKm < 10 ? 1 : 0)} km · ${MODES.find((m) => m.id === mode).label}`
      )
    );
    // Beim Velo zaehlt mehr als Zeit und Strecke: Hoehenmeter und die Frage,
    // ob man neben Lastwagen faehrt. Beides kommt aus zwei Nachfragen und wird
    // nachgetragen, sobald es da ist - die Route steht schon.
    if (mode === 'bicycle' && res.shape) {
      const kasten = document.createElement('div');
      kasten.className = 'velo__auswertung';
      nodes.result.append(kasten);
      veloAuswertung(res, kasten, signal);
    }

    const list = document.createElement('ol');
    list.className = 'route__steps';
    res.steps.forEach((step, i) => {
      const li = document.createElement('li');
      // Ein Schritt ist ein Ort: antippen zeigt die Stelle auf der Karte.
      const b = document.createElement('button');
      b.type = 'button';
      const t = document.createElement('span');
      t.textContent = step.text;
      const d = document.createElement('em');
      d.textContent = step.km >= 1 ? `${km(step.km)} km` : `${Math.round(step.km * 1000)} m`;
      b.append(t, d);
      b.addEventListener('click', () => {
        const c = res.coordinates[res.maneuvers?.[i]?.von];
        if (c) map.flyTo({ center: c, zoom: Math.max(map.getZoom(), 16), duration: 600 });
      });
      li.append(b);
      list.append(li);
    });
    nodes.result.append(list, reiseKnopf());
  }

  /** Knopf fuer die Leiste unter der Liste. */
  function pagerKnopf(text, richtung) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'route__page';
    b.textContent = text;
    b.addEventListener('click', () => verschieben(richtung));
    return b;
  }

  /**
   * Frueher und spaeter.
   *
   * Spaeter ist einfach: ab einer Minute nach der letzten gezeigten Abfahrt.
   *
   * Frueher ist es nicht, denn der Fahrplan antwortet immer vorwaerts ab einem
   * Zeitpunkt - eine Seite zurueck kennt er nicht. Der Umweg ueber die Ankunft
   * taugt auch nicht: dort stehen die Verbindungen, die bis dahin ankommen,
   * und darunter sind schnellere, die spaeter abfahren. Also wird schrittweise
   * weiter zurueck gefragt, bis wirklich Frueheres in der Antwort steht - auf
   * dem Land faehrt der Bus auch mal zweistuendlich.
   */
  const RUECKSCHRITTE = [60, 150, 420];   // Minuten

  async function verschieben(richtung) {
    if (!connections.length) return;
    const abfahrten = connections.map((c) => new Date(c.departure).getTime());

    if (richtung > 0) {
      suche = { zeit: new Date(Math.max(...abfahrten) + 60000), ankunft: false };
      run();
      return;
    }

    const erste = Math.min(...abfahrten);
    const vorher = suche;
    for (const minuten of RUECKSCHRITTE) {
      suche = { zeit: new Date(erste - minuten * 60000), ankunft: false };
      if (await run({ frueherAls: erste })) return;
    }
    // Nichts Frueheres: die alte Liste bleibt stehen, damit der Druck auf den
    // Knopf nicht die Ansicht leer raeumt.
    suche = vorher;
    renderTransit();
    selectConnection(0);
    toast('Keine früheren Verbindungen gefunden.');
  }

  /** Ein Kilometerwert, wie man ihn liest: unter zehn mit einer Stelle. */
  const km = (x) => (x >= 10 ? Math.round(x) : Math.round(x * 10) / 10)
    .toString().replace('.', ',');

  /**
   * Höhenprofil als Fläche unter der Kurve.
   *
   * Die Höhenachse spannt immer mindestens fünfzig Meter: sonst macht die
   * Kurve aus zwanzig Metern Unterschied eine Alpenüberquerung.
   */
  function hoehenprofil(h) {
    const W = 320;
    const H = 64;
    const RAND = { oben: 8, unten: 14, links: 28, rechts: 4 };
    const innenB = W - RAND.links - RAND.rechts;
    const innenH = H - RAND.oben - RAND.unten;
    let lo = h.min;
    let hi = h.max;
    if (hi - lo < 50) { const m = (lo + hi) / 2; lo = m - 25; hi = m + 25; }
    const x = (m) => RAND.links + (innenB * m) / Math.max(1, h.laengeM);
    const y = (v) => RAND.oben + innenH * (1 - (v - lo) / (hi - lo));

    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('class', 'velo__profil');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label',
      `Höhenprofil: ${h.aufstieg} Meter aufwärts, ${h.abstieg} Meter abwärts, ${h.min} bis ${h.max} m ü. M.`);

    const d = h.punkte.map((p, i) => `${i ? 'L' : 'M'}${x(p.m).toFixed(1)},${y(p.h).toFixed(1)}`).join('');
    const flaeche = document.createElementNS(ns, 'path');
    flaeche.setAttribute('d', `${d}L${x(h.laengeM).toFixed(1)},${RAND.oben + innenH}L${RAND.links},${RAND.oben + innenH}Z`);
    flaeche.setAttribute('class', 'velo__profil-flaeche');
    const linie = document.createElementNS(ns, 'path');
    linie.setAttribute('d', d);
    linie.setAttribute('class', 'velo__profil-linie');
    svg.append(flaeche, linie);

    for (const v of [hi, lo]) {
      const t = document.createElementNS(ns, 'text');
      t.setAttribute('x', RAND.links - 5);
      t.setAttribute('y', y(v) + 3);
      t.setAttribute('text-anchor', 'end');
      t.setAttribute('class', 'velo__profil-achse');
      t.textContent = Math.round(v);
      svg.append(t);
    }
    const ende = document.createElementNS(ns, 'text');
    ende.setAttribute('x', W - RAND.rechts);
    ende.setAttribute('y', H - 4);
    ende.setAttribute('text-anchor', 'end');
    ende.setAttribute('class', 'velo__profil-achse');
    ende.textContent = `${km(h.laengeM / 1000)} km`;
    svg.append(ende);
    return svg;
  }

  /**
   * Höhenmeter und Streckenart nachtragen.
   *
   * Zwei zusätzliche Abfragen an denselben Dienst. Scheitert eine, fehlt nur
   * ihr Teil - die Route bleibt stehen.
   */
  async function veloAuswertung(res, kasten, signal) {
    const [h, teile] = await Promise.all([
      fetchHoehenprofil({ shape: res.shape, signal }),
      fetchVeloAbschnitte({ shape: res.shape, signal })
    ]);
    if (signal?.aborted || !kasten.isConnected) return;

    if (h) {
      const zeile = document.createElement('div');
      zeile.className = 'velo__hoehe';
      zeile.innerHTML = `<strong>&#9650; ${h.aufstieg} m</strong>`
        + `<strong>&#9660; ${h.abstieg} m</strong>`
        + `<span>${h.min}–${h.max} m ü. M.</span>`;
      kasten.append(zeile, hoehenprofil(h));
    }

    if (teile && teile.gesamt > 0) {
      // Die vier Töpfe teilen die Strecke auf und ergeben zusammen die
      // Gesamtlänge. Jeder trägt seinen Farbpunkt - damit ist die Aufzählung
      // zugleich die Legende zur eingefärbten Linie auf der Karte und braucht
      // keine zweite Tabelle daneben.
      const liste = document.createElement('div');
      liste.className = 'velo__teile';
      for (const art of ['getrennt', 'streifen', 'hauptOhne', 'uebrig']) {
        if (teile[art] <= 0.05) continue;
        const span = document.createElement('span');
        span.className = 'velo__teil';
        const punkt = document.createElement('i');
        punkt.style.background = ABSCHNITT_FARBEN[art];
        span.append(punkt, document.createTextNode(`${km(teile[art])} km ${ABSCHNITT_TEXTE[art]}`));
        liste.append(span);
      }
      if (liste.children.length) kasten.append(liste);

      // Belag und Routennetz stehen in einem eigenen Satz, weil sie dieselben
      // Kilometer von einer anderen Seite beschreiben.
      const quer = [];
      if (teile.netz > 0.05) quer.push(`${km(teile.netz)} km auf signalisierten Velorouten`);
      if (teile.unbefestigt > 0.05) quer.push(`${km(teile.unbefestigt)} km unbefestigt`);
      if (quer.length) {
        const p = document.createElement('p');
        p.className = 'hint hint--block';
        p.textContent = quer.join(' · ');
        kasten.append(p);
      }

      // Dieselbe Linie nochmals zeichnen, jetzt in Stücken nach Art. Der
      // Ausschnitt bleibt, wie er ist - die Route hat sich nicht geändert,
      // nur ihre Farbe.
      if (teile.abschnitte?.length) {
        drawRoute(
          teile.abschnitte.map((a) => ({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: a.coords },
            properties: { art: a.art }
          })),
          [],
          { einpassen: false }
        );
      }
    }
  }

  function renderTransit() {
    nodes.result.textContent = '';

    // Welche Haltestellen der Fahrplan genommen hat - bei einer Adresse als
    // Start ist das die naechste Station, und das soll man sehen.
    if (stationen?.von?.name && stationen?.nach?.name) {
      const kopf = document.createElement('p');
      kopf.className = 'route__stations';
      kopf.textContent = `ab ${stationen.von.name} · an ${stationen.nach.name}`;
      nodes.result.append(kopf);
    }

    const list = document.createElement('div');
    list.className = 'route__connections';
    connections.forEach((c, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'route__conn';
      btn.dataset.index = String(i);

      const head = document.createElement('strong');
      head.textContent = `${hhmm(c.departure)} – ${hhmm(c.arrival)}`;
      const meta = document.createElement('span');
      meta.textContent = [
        tagWennAnders(c.departure),
        c.duration != null ? `${c.duration} Min` : null,
        c.transfers ? `${c.transfers} Umstiege` : 'direkt'
      ].filter(Boolean).join(' · ');
      const lines = document.createElement('span');
      lines.className = 'route__lines';
      // Produktzeichen statt Text - so steht in der Liste, was auch am Gleis
      // und am Fahrzeug steht.
      const fahrten = c.legs.filter((l) => l.line);
      if (!fahrten.length) lines.textContent = 'Fussweg';
      fahrten.forEach((l, k) => {
        if (k) lines.append(' › ');
        lines.append(linienZeichen(l.kategorie, l.nummer));
      });

      btn.append(head, meta, lines);
      btn.addEventListener('click', () => selectConnection(i));
      list.append(btn);
    });

    const leiste = document.createElement('div');
    leiste.className = 'route__pager';
    leiste.append(pagerKnopf('‹ früher', -1), pagerKnopf('später ›', 1));

    nodes.result.append(list, leiste, reiseKnopf());
  }

  /** Die Abschnitte der gewählten Verbindung: wann, wo, welches Gleis, wie weiter. */
  function abschnitte(c) {
    const box = document.createElement('div');
    box.className = 'route__legs';
    for (const l of c.legs) {
      const z = document.createElement('div');
      z.className = 'route__leg';
      if (l.walk) {
        z.classList.add('route__leg--fuss');
        z.textContent = `Fussweg nach ${l.toName || 'dem nächsten Halt'}`;
        box.append(z);
        continue;
      }
      const zeile = (zeit, ort, zusatz) => {
        const r = document.createElement('div');
        r.className = 'route__leg-halt';
        const t = document.createElement('strong');
        t.textContent = hhmm(zeit);
        const o = document.createElement('span');
        o.textContent = ort || '';
        r.append(t, o);
        if (zusatz) {
          const g = document.createElement('em');
          g.textContent = zusatz;
          r.append(g);
        }
        return r;
      };
      const linie = document.createElement('div');
      linie.className = 'route__leg-linie';
      if (l.line) linie.append(linienZeichen(l.kategorie, l.nummer));
      if (l.to) linie.append(` Richtung ${l.to}`);
      z.append(
        zeile(l.departure, l.fromName, l.platform ? `Gl. ${l.platform}` : null),
        linie,
        zeile(l.arrival, l.toName)
      );
      box.append(z);
    }
    return box;
  }

  /** „Reise speichern“ - landet als Favorit und kommt von dort zurueck. */
  function reiseKnopf() {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'route__save';
    b.textContent = 'Reise speichern';
    b.addEventListener('click', () => {
      if (!points.from || !points.to) return;
      onSaveTrip?.({
        from: points.from,
        to: points.to,
        mode,
        zeit: eingabe.zeit ? eingabe.zeit.toISOString() : null,
        ankunft: eingabe.ankunft
      });
    });
    return b;
  }

  /**
   * Eine Verbindung zeichnen.
   *
   * Der Fahrplan liefert je Abschnitt nur die Haltestellen - direkt verbunden
   * ergibt das eine Luftlinie quer durchs Gelaende. Deshalb wird zuerst nur
   * die Haltekette als Punkte gezeigt und dann je Abschnitt der tatsaechliche
   * Verlauf geholt: Fahrten aus den OSM-Linienrelationen, Fusswege ueber
   * Valhalla. Was sich so nicht belegen laesst, bleibt ungezeichnet.
   */
  async function selectConnection(index) {
    const c = connections[index];
    if (!c) return;
    const lauf = ++zeichenLauf;
    nodes.result.querySelector('.route__legs')?.remove();
    for (const b of nodes.result.querySelectorAll('.route__conn')) {
      const aktiv = Number(b.dataset.index) === index;
      b.classList.toggle('is-active', aktiv);
      b.setAttribute('aria-expanded', String(aktiv));
      if (aktiv) b.after(abschnitte(c));
    }

    const stops = connectionGeometry(c);
    drawRoute([], stops);

    const strecken = await Promise.all(c.legs.map(async (l) => {
      const halte = l.halte || [];
      if (halte.length < 2) return null;
      const erster = halte[0];
      const letzter = halte[halte.length - 1];
      if (l.walk) {
        const weg = await routeFootpath({ from: erster, to: letzter }).catch(() => null);
        // Der Umstieg von Gleis zu Gleis ist kein Weg, den man zeichnen muss.
        if (!(weg?.length > 1)) return { kurz: true };
        return { coordinates: weg, walk: true };
      }
      const v = await fahrtVerlauf({
        halte, kategorie: l.kategorie, ref: l.kuerzel, nummer: l.nummer, ziel: l.to
      }).catch(() => null);
      return v ? { coordinates: v.coordinates, walk: false } : null;
    }));

    if (lauf !== zeichenLauf) return;            // inzwischen anders entschieden
    const features = strecken.filter((t) => t?.coordinates).map((t) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: t.coordinates },
      properties: { walk: t.walk }
    }));
    // Der Ausschnitt steht schon auf den Halten. Nochmals einpassen hiesse, die
    // Karte unter jemandem wegzuziehen, der sie inzwischen verschoben hat.
    drawRoute(features, stops, { einpassen: false });

    const fehlend = strecken.filter((t) => !t).length;
    if (fehlend) {
      const welche = c.legs.filter((l, i) => !strecken[i] && !l.walk).map((l) => l.line).filter(Boolean);
      toast(welche.length
        ? `Für ${welche.join(', ')} ist in OpenStreetMap kein Linienverlauf hinterlegt.`
        : `${fehlend === 1 ? 'Ein Abschnitt' : `${fehlend} Abschnitte`} ohne gesicherten Verlauf – nur die Halte.`);
    }
  }
  /* ---------------------------------------------------------------- Mount */

  function mount(n) {
    nodes = n;

    for (const m of MODES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(m.id === mode));
      btn.textContent = m.label;
      btn.addEventListener('click', () => {
        mode = m.id;
        merken();
        for (const other of n.modeHost.children) other.setAttribute('aria-checked', 'false');
        btn.setAttribute('aria-checked', 'true');
        zeitfelderZeigen();
        run();
      });
      n.modeHost.append(btn);
    }

    /** Datum und Uhrzeit auf den aktuellen Zeitpunkt stellen. */
    function zeitfelderFuellen() {
      const jetzt = new Date();
      if (n.dateInput) n.dateInput.value = alsFeldDatum(jetzt);
      if (n.timeInput) n.timeInput.value = alsFeldZeit(jetzt);
    }

    /* Abfahrtszeit nur beim OeV - bei Auto und Velo gibt es keinen Fahrplan. */
    function zeitfelderZeigen() {
      if (n.whenBox) n.whenBox.hidden = mode !== 'transit';
      if (n.veloBox) n.veloBox.hidden = mode !== 'bicycle';
      if (mode === 'transit' && !zeitBeruehrt) zeitfelderFuellen();
    }
    feldSichtbarkeit = zeitfelderZeigen;
    // Veloeinstellungen verdrahten. Jede Aenderung rechnet die Route sofort
    // neu - der Unterschied zwischen "Steigungen meiden" und nicht ist genau
    // das, was man sehen will.
    if (n.veloTyp) {
      for (const t of VELOTYPEN) {
        const o = document.createElement('option');
        o.value = t.id;
        o.textContent = `${t.label} · ${t.tempo} km/h`;
        n.veloTyp.append(o);
      }
      n.veloTyp.value = velo.typ;
      n.veloTyp.addEventListener('change', () => { velo = { ...velo, typ: n.veloTyp.value }; merken(); veloZeile(); run(); });
    }
    for (const [schluessel, el] of [['huegel', n.veloHuegel], ['verkehr', n.veloVerkehr], ['belag', n.veloBelag]]) {
      if (!el) continue;
      el.checked = velo[schluessel];
      el.addEventListener('change', () => { velo = { ...velo, [schluessel]: el.checked }; merken(); veloZeile(); run(); });
    }
    /** Zugeklappt sagt die Zeile, was eingestellt ist - sonst vergisst man es. */
    function veloZeile() {
      if (!n.veloZusammenfassung) return;
      const typ = VELOTYPEN.find((t) => t.id === velo.typ);
      const meiden = [velo.huegel && 'Steigungen', velo.verkehr && 'Verkehr', velo.belag && 'Beläge'].filter(Boolean);
      n.veloZusammenfassung.textContent = [typ?.label, meiden.length ? `meidet ${meiden.join(', ')}` : null]
        .filter(Boolean).join(' · ');
    }
    veloZeile();

    zeitfelderZeigen();
    // Damit die Felder auch beim Oeffnen der Spalte stimmen, nicht erst beim
    // Umschalten auf OeV.
    aktuelleZeitAnzeigen = () => { if (!zeitBeruehrt) zeitfelderFuellen(); };

    /**
     * Zeitpunkt aus den Feldern. Leer heisst „jetzt“ - dann fragt der Fahrplan
     * nach der naechsten Verbindung, so wie beim Oeffnen.
     */
    function zeitLesen() {
      const d = n.dateInput?.value;
      const t = n.timeInput?.value;
      if (!d && !t) return null;
      const basis = new Date();
      const [jahr, monat, tag] = (d || alsFeldDatum(basis)).split('-').map(Number);
      const [std, min] = (t || alsFeldZeit(basis)).split(':').map(Number);
      const wann = new Date(jahr, monat - 1, tag, std, min, 0, 0);
      return Number.isNaN(wann.getTime()) ? null : wann;
    }

    function zeitUebernehmen() {
      zeitBeruehrt = true;
      eingabe = { zeit: zeitLesen(), ankunft: eingabe.ankunft };
      suche = { ...eingabe };
      if (mode === 'transit') run();
    }

    n.dateInput?.addEventListener('change', zeitUebernehmen);
    n.timeInput?.addEventListener('change', zeitUebernehmen);

    /** „ab“ oder „an“: meint die Zeit die Abfahrt oder die Ankunft? */
    for (const [knopf, istAnkunft] of [[n.depBtn, false], [n.arrBtn, true]]) {
      knopf?.addEventListener('click', () => {
        eingabe = { zeit: zeitLesen(), ankunft: istAnkunft };
        suche = { ...eingabe };
        n.depBtn?.setAttribute('aria-checked', String(!istAnkunft));
        n.arrBtn?.setAttribute('aria-checked', String(istAnkunft));
        if (mode === 'transit') run();
      });
    }

    n.nowBtn?.addEventListener('click', () => {
      zeitBeruehrt = false;
      zeitfelderFuellen();
      eingabe = { zeit: null, ankunft: false };
      suche = { ...eingabe };
      n.depBtn?.setAttribute('aria-checked', 'true');
      n.arrBtn?.setAttribute('aria-checked', 'false');
      if (mode === 'transit') run();
    });

    const lookup = async (which, input) => {
      const q = input.value.trim();
      // Der Text steht schon für diesen Punkt: etwa nach einer Wahl aus der Liste.
      if (q === (points[which]?.name || '')) return;
      if (!q) return setPoint(which, null);
      try {
        setPoint(which, await geocode(q, map));
      } catch (err) {
        toast(err.message);
      }
    };
    ortsfeld({ input: n.fromInput, map, onWahl: (p) => setPoint('from', p) });
    ortsfeld({ input: n.toInput, map, onWahl: (p) => setPoint('to', p) });
    /**
     * Eigener Standort als Routenpunkt. Die Ortung kann dauern und
     * fehlschlagen - solange laeuft der Knopf sichtbar, danach steht
     * entweder der Punkt oder eine Meldung.
     */
    function standortUebernehmen(which, knopf) {
      if (!navigator.geolocation) return;
      const vorher = knopf.textContent;
      knopf.textContent = '…';
      knopf.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          knopf.textContent = vorher;
          knopf.disabled = false;
          setPoint(which, {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            name: 'Mein Standort'
          });
        },
        () => {
          knopf.textContent = vorher;
          knopf.disabled = false;
          knopf.title = 'Standort nicht verfügbar';
          toast('Standort nicht verfügbar. Ist die Ortung erlaubt?');
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
      );
    }
    n.hereFrom?.addEventListener('click', () => standortUebernehmen('from', n.hereFrom));
    n.hereTo?.addEventListener('click', () => standortUebernehmen('to', n.hereTo));

    n.fromInput.addEventListener('change', () => lookup('from', n.fromInput));
    n.toInput.addEventListener('change', () => lookup('to', n.toInput));

    n.swapBtn.addEventListener('click', () => {
      // Nur einmal rechnen, nicht je Feld - dazwischen fehlte sonst ein Punkt.
      const a = points.from;
      setPoint('from', points.to, { rechnen: false });
      setPoint('to', a, { rechnen: false });
      run();
    });

    n.clearBtn.addEventListener('click', () => {
      setPoint('from', null, { rechnen: false });
      setPoint('to', null);
      connections = [];
      stationen = null;
      nodes.result.textContent = '';
      clearRoute();
    });
  }

  /** setStyle wirft eigene Quellen und Ebenen weg - hier kommen sie zurück. */
  function redraw() {
    if (!drawn) return;
    const { features, stops } = drawn;
    ensureLayers();
    map.getSource('route').setData({ type: 'FeatureCollection', features });
    map.getSource('route-stops').setData({
      type: 'FeatureCollection',
      features: stops.map((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} }))
    });
  }

  /**
   * Eine gespeicherte Reise wieder aufnehmen: Punkte, Verkehrsmittel und, wenn
   * eine feste Zeit dazugehoert, auch die Felder.
   */
  function restore(reise) {
    if (!reise) return;
    mode = reise.mode || 'transit';
    if (nodes?.modeHost) {
      for (const [i, m] of MODES.entries()) {
        nodes.modeHost.children[i]?.setAttribute('aria-checked', String(m.id === mode));
      }
    }
    // Velo-Einstellungen und Zeitfelder gehören zum Verkehrsmittel. Bisher
    // blieben sie beim Aufnehmen einer Reise stehen, wie sie gerade waren.
    feldSichtbarkeit?.();
    const wann = reise.zeit ? new Date(reise.zeit) : null;
    // Ein alter Zeitpunkt hilft nicht - dann gilt wieder „jetzt“.
    const gueltig = wann && !Number.isNaN(wann.getTime()) && wann.getTime() > Date.now() ? wann : null;
    eingabe = { zeit: gueltig, ankunft: Boolean(reise.ankunft) };
    suche = { ...eingabe };
    zeitBeruehrt = Boolean(gueltig);
    const anzeige = gueltig || new Date();
    if (nodes?.dateInput) nodes.dateInput.value = alsFeldDatum(anzeige);
    if (nodes?.timeInput) nodes.timeInput.value = alsFeldZeit(anzeige);
    nodes?.depBtn?.setAttribute('aria-checked', String(!eingabe.ankunft));
    nodes?.arrBtn?.setAttribute('aria-checked', String(eingabe.ankunft));
    setPoint('to', reise.to || null, { rechnen: false });
    setPoint('from', reise.from || null);
  }

  /** Wie das Teilen die aktuelle Route beschreibt. */
  const fuerLink = () => (points.from && points.to ? { mode, from: points.from, to: points.to } : null);

  return { mount, setPoint, redraw, restore, fuerLink, hasPoint: (which) => Boolean(points[which]) };
}
